import { useEffect, useState } from 'react';
import { BrandLink } from '../../../_Templates/_UI/_Shared/BrandLink';
import { FaBars, FaTimes, FaClock } from 'react-icons/fa';
import { Link, useLocation } from 'react-router-dom';
import { NavList } from './NavList';
import { NavPanel } from './NavPanel';
import { useLanguage } from '../../../../../_CORE/Contexts/Translate/useLanguageContext';
import { onResolveHeaderTemplate } from '../../../_Templates/_Config/HeaderTemplates';
import { HEADER_TEMPLATE_EVENT, onReadHeaderTemplate } from '../../../_Templates/AppTemplateResolved';
import { LAYOUTS } from '../../../../../_CORE/_RuntimeConfig/_Config/_Layouts.js';

const BLOCK = 'LStudio__app_header';

const MOBILE_BREAKPOINT = LAYOUTS.SHELL.HEADER_MOBILE_BREAKPOINT;

/*
    🔥 EL HEADER, UNA SOLA VEZ

    Público  -> marca + navegación + iconos
    Privado  -> SOLO iconos (la marca y la nav viven en el sidebar),
                más el botón que abre el sidebar en mobile.

    ⚠️ Los iconos van FUERA de la nav: no colapsan nunca.
*/

const PRIVATE_LIVE_SESSION_EVENT = 'scooldata:private-live-session';
const PRIVATE_LIVE_SESSION_KEY = 'scooldata.privateLiveSession';

const readPrivateLiveSession = () => {
    try {
        const raw = window.sessionStorage.getItem(PRIVATE_LIVE_SESSION_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch { return null; }
};

const PrivateLiveSessionPlayer = () => {
    const [session, setSession] = useState(() => readPrivateLiveSession());
    const [now, setNow] = useState(Date.now());

    useEffect(() => {
        const onSession = (event) => setSession(event.detail || readPrivateLiveSession());
        window.addEventListener(PRIVATE_LIVE_SESSION_EVENT, onSession);
        return () => window.removeEventListener(PRIVATE_LIVE_SESSION_EVENT, onSession);
    }, []);

    useEffect(() => {
        if (!session?.startedAt) return undefined;
        const timer = window.setInterval(() => setNow(Date.now()), 1000);
        return () => window.clearInterval(timer);
    }, [session?.startedAt]);

    if (!session?.startedAt) return null;
    const start = new Date(String(session.startedAt).replace(' ', 'T')).getTime();
    const total = Number.isFinite(start) ? Math.max(0, Math.floor((now - start) / 1000)) : 0;
    const h = String(Math.floor(total / 3600)).padStart(2, '0');
    const m = String(Math.floor((total % 3600) / 60)).padStart(2, '0');
    const sec = String(total % 60).padStart(2, '0');

    return <div className="SDPrivateLivePlayer" role="status" aria-live="polite">
        <span className="SDPrivateLivePlayer__pulse"><FaClock /></span>
        <span className="SDPrivateLivePlayer__copy"><small>CLASE EN CURSO</small><strong>{session.title || 'Clase'}</strong></span>
        <b>{h}:{m}:{sec}</b>
    </div>;
};

export const HeaderShell = ({
    templateId,
    isPrivate = false,
    brand = null,
    homePath = '/',
    navItems = [],
    utilities = null,
    extraTop = null,
    extraType = 'ad',
    onToggleAside = null,
}) => {

    const SCOPE = isPrivate ? 'private' : 'public';
    const SLOT = isPrivate ? brand?.headerPrivate : brand?.headerPublic;

    const [useTemplateId, setTemplateId] = useState(() => onReadHeaderTemplate(SCOPE, templateId));
    const { useDataLanguage } = useLanguage();

    const NAV_TEXT = useDataLanguage.translations?.TEMPLATES?.nav ?? {};

    const [useIsOpen, setIsOpen] = useState(false);
    const [useIsMobile, setIsMobile] = useState(() => window.innerWidth < MOBILE_BREAKPOINT);

    const TEMPLATE = onResolveHeaderTemplate(useTemplateId);

    const { pathname } = useLocation();

    // 🔥 Cambio de disposición en caliente (panel de prueba)
    useEffect(() => {

        const onChange = (event) => {
            if (event.detail?.scope !== SCOPE) return;
            setTemplateId(event.detail.id);
            setIsOpen(false);
        };

        window.addEventListener(HEADER_TEMPLATE_EVENT, onChange);
        return () => window.removeEventListener(HEADER_TEMPLATE_EVENT, onChange);

    }, [SCOPE]);

    // 🔥 Cierra al navegar
    useEffect(() => { setIsOpen(false); }, [pathname]);

    // 🔥 Cierra al cruzar el breakpoint
    useEffect(() => {

        const onResize = () => {
            const IS_MOBILE = window.innerWidth < MOBILE_BREAKPOINT;
            setIsMobile((prev) => { if (prev !== IS_MOBILE) setIsOpen(false); return IS_MOBILE; });
        };

        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);

    }, []);

    // 🔥 Cierra con Escape
    useEffect(() => {

        const onEscape = (event) => { if (event.key === 'Escape') setIsOpen(false); };

        document.addEventListener('keydown', onEscape);
        return () => document.removeEventListener('keydown', onEscape);

    }, []);

    const HAS_NAV = navItems.length > 0;

    // Colapsa en mobile siempre; en escritorio solo si el template lo pide.
    const IS_COLLAPSED = HAS_NAV && (useIsMobile || TEMPLATE.always || TEMPLATE.nav === 'hidden');

    const SHOW_INLINE_NAV = HAS_NAV && !IS_COLLAPSED && TEMPLATE.nav !== 'row_below';

    const SHOW_ROW_NAV = HAS_NAV && !IS_COLLAPSED && TEMPLATE.nav === 'row_below';

    return (
        <header className={`${BLOCK} ${isPrivate ? `${BLOCK}--private` : ''}`}
            data-header={TEMPLATE.id}
            data-brand={TEMPLATE.brand}
            data-nav={TEMPLATE.nav}
            data-collapse={TEMPLATE.collapse}
            data-collapsed={IS_COLLAPSED} >

            {extraTop && <div className={`${BLOCK}__extra`} data-extra={extraType}>{extraTop}</div>}

            <div className={`${BLOCK}__bar`}>

                {
                    onToggleAside && (
                        <button type="button" className={`${BLOCK}__aside_toggle`}
                            aria-label={NAV_TEXT.openAside} onClick={onToggleAside} >
                            <FaBars />
                        </button>
                    )
                }

                {
                    brand && (
                        <BrandLink slot={SLOT} homePath={homePath} className={`${BLOCK}__brand`} isLogo={SLOT?.isLogo === true}>
                            {SLOT?.isLogo && SLOT?.logoLight && (
                                <img src={SLOT.logoLight} alt={SLOT?.alt ?? ""} title={SLOT?.title ?? ""}
                                    className={`${BLOCK}__logo ${BLOCK}__logo--light`}
                                    onError={(e) => { e.currentTarget.style.display = "none"; }} />
                            )}

                            {SLOT?.isLogo && SLOT?.logoDark && (
                                <img src={SLOT.logoDark} alt={SLOT?.alt ?? ""} title={SLOT?.title ?? ""}
                                    className={`${BLOCK}__logo ${BLOCK}__logo--dark`}
                                    onError={(e) => { e.currentTarget.style.display = "none"; }} />
                            )}

                            {!SLOT?.isLogo && <span className={`${BLOCK}__brand_name`}>{brand?.name}</span>}

                        </BrandLink>
                    )
                }

                {
                    SHOW_INLINE_NAV && (
                        <nav className={`${BLOCK}__nav`} aria-label={NAV_TEXT.main}>
                            <NavList items={navItems} />
                        </nav>
                    )
                }

                {isPrivate && pathname.startsWith("/instructor") && <PrivateLiveSessionPlayer />}

                {/* 🔥 SIEMPRE VISIBLE — no colapsa en ningún template ni resolución */}
                <div className={`${BLOCK}__actions`}>{utilities}</div>

                {
                    IS_COLLAPSED && (
                        <button type="button" className={`${BLOCK}__toggle`}
                            aria-label={useIsOpen ? NAV_TEXT.closeMenu : NAV_TEXT.openMenu}
                            aria-expanded={useIsOpen}
                            onClick={() => setIsOpen(!useIsOpen)} >
                            {useIsOpen ? <FaTimes /> : <FaBars />}
                        </button>
                    )
                }

            </div>

            {
                SHOW_ROW_NAV && (
                    <nav className={`${BLOCK}__row`} aria-label={NAV_TEXT.main}>
                        <NavList items={navItems} />
                    </nav>
                )
            }

            {
                IS_COLLAPSED && (
                    <NavPanel mode={TEMPLATE.collapse} title={NAV_TEXT.menu} isOpen={useIsOpen}
                        onClose={() => setIsOpen(false)} items={navItems} />
                )
            }

        </header>
    );
};
